/**
 * Канал текущего режима публикаций для бренда — и ответ на вопрос «а он вообще задан?».
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ХУК. Канал выбирает режим, а поле режима в конфиге бренда может быть
 * пустым: боевой канал заводится позже тестового, и до этого момента любое одобрение в
 * боевом режиме обязано отказать (`approve.ts` → `channel_not_configured`). Отказ бэкенда —
 * последний рубеж, но человек приходит к нему, уже выбрав слот и удержав кнопку 800 мс.
 * Экран обязан сказать это раньше и одними словами на обоих экранах утверждения, иначе
 * «пост не ушёл» выглядит как сбой системы, а не как незаполненное поле конфига.
 *
 * КАНАЛ — ЧАСТЬ ЗАПРОСА (2026-10-25, изоляция брендов). Одобрение, пакет и перенос несут
 * `expected_chat_id` — канал, который назвал экран (`decisions.ts` контрактов), и бэкенд отказывает
 * `channel_mismatch`, если подписал бы другой. Поэтому канал, который не подтверждён (`loading`,
 * `failed`, `malformed`), теперь закрывает действие так же, как незаданный: назвать бэкенду
 * нечего, а подписать «какой-нибудь» канал экран не вправе. `readyChatId` — единственный способ
 * достать id для запроса.
 *
 * ПОЧЕМУ `/api/v1/brands`, А НЕ НОВАЯ РУЧКА. `BrandSchema.channel_id` с 10.09.2026 — это
 * канал ТЕКУЩЕГО режима или `null` (`read-model.ts` → `brandsList`). Один источник правды на
 * весь фронт: спроси экран конфиг отдельно, он завёл бы второе место, знающее связку
 * «режим → поле», — ровно то, что §Окружение 5.1 запрещает в сервисах.
 */

import { BrandsResponseSchema, type PublicationDispatchState } from '@smm/contracts/schemas';
import type { BrandsResponse } from './types.ts';
import { useResource } from './useResource.ts';

export type ModeChannel =
  /** Список брендов ещё едет. */
  | { readonly kind: 'loading' }
  /** Запрос провалился: про канал ничего не известно. */
  | { readonly kind: 'failed' }
  /** Бренда нет в ответе — его конфиг не загрузился на бэкенде. */
  | { readonly kind: 'unknown' }
  /** Ответ пришёл, но он не похож на список брендов: про канал ничего не известно. */
  | { readonly kind: 'malformed' }
  /** Поле канала текущего режима пусто: одобрять в этом режиме нельзя. */
  | { readonly kind: 'missing' }
  | { readonly kind: 'ready'; readonly channelId: string };

export interface ModeChannelResource {
  readonly channel: ModeChannel;
  readonly refetch: () => void;
}

export function useModeChannel(brand: string): ModeChannelResource {
  const brands = useResource<BrandsResponse>('/api/v1/brands');
  return { channel: channelOf(brands.data, brands.error, brand), refetch: brands.refetch };
}

/**
 * Ответ разбирается схемой, а не берётся на веру: тип `useResource` — это обещание, а не
 * проверка, и «не список» здесь стоил бы упавшего диалога утверждения вместо строчки о том,
 * что канал неизвестен. Ровно это и произошло, когда хук впервые встал в диалог.
 */
function channelOf(
  data: BrandsResponse | undefined,
  error: Error | undefined,
  brand: string,
): ModeChannel {
  if (data === undefined) return error === undefined ? { kind: 'loading' } : { kind: 'failed' };
  const parsed = BrandsResponseSchema.safeParse(data);
  if (!parsed.success) return { kind: 'malformed' };
  const found = parsed.data.find((b) => b.id === brand);
  if (found === undefined) return { kind: 'unknown' };
  return found.channel_id === null
    ? { kind: 'missing' }
    : { kind: 'ready', channelId: found.channel_id };
}

/** Id канала для `expected_chat_id`; `null` — канал не подтверждён, и действие закрыто. */
export function readyChatId(channel: ModeChannel): string | null {
  return channel.kind === 'ready' ? channel.channelId : null;
}

/**
 * Почему утверждать нельзя — одним текстом на оба экрана. Режим назван словом, потому что
 * решать человеку: дождаться заполненного канала или перевести режим и одобрить в другой.
 */
export function channelMissingText(mode: PublicationDispatchState | undefined): string {
  const word = mode === 'live' ? 'Боевой' : mode === 'test' ? 'Тестовый' : 'Текущий';
  return (
    `${word} канал бренда не задан в конфиге — подписывать пост не на что: канал входит в ` +
    'подпись ключа. Впишите id канала в конфиг бренда или переключите режим.'
  );
}

/** Бренда нет в списке: его конфиг не загрузился на бэкенде, и канала у экрана нет. */
export const CHANNEL_BRAND_UNKNOWN_TEXT =
  'Бренда нет в списке брендов бэкенда — его конфиг не загрузился, и канал неизвестен. ' +
  'Утверждение откажет на подписи; смотрите журнал бэкенда.';

/**
 * Запрос брендов провалился или ответ не разобрался: канал не подтверждён и не опровергнут. С
 * 2026-10-25 это отказ, а не предупреждение: запрос обязан назвать канал (`expected_chat_id`).
 */
export const CHANNEL_UNKNOWN_TEXT =
  'Не удалось узнать канал текущего режима: список брендов не загрузился. Утверждение называет ' +
  'канал в запросе, поэтому без него недоступно — обновите экран.';

/** Список брендов ещё едет. */
export const CHANNEL_LOADING_TEXT = 'Загружаем канал текущего режима…';
